import React from 'react'
import { cn, formatPrice } from '@/lib/utils'

import { Separator } from '@/components/ui'

interface ReceiptProps {
  data: any
}

export const Receipt = ({ data }: ReceiptProps) => {
  return (
    <div className="flex w-full flex-col gap-4 px-4 text-sm md:text-base lg:px-10">
      <div className="flex w-full flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="font-mono">Invoice No</span>
          <span className="font-mono">{data?.order?.invoice_number}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono">Order No</span>
          <span className="font-mono">{data?.order?.id}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono">Date</span>
          <span className="font-mono">{data?.order?.created_at}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono">Payment</span>
          <span className="font-mono">{data?.order?.status_payment}</span>
        </div>
      </div>

      <div className="flex w-full flex-col gap-1">
        <Separator />

        <div className="grid grid-cols-[1fr_10fr]">
          <span className="font-mono font-medium">#</span>
          <div className="grid grid-cols-[5fr_1fr_2fr_2fr]">
            <span className="font-mono font-medium">Desc</span>
            <span className="font-mono font-medium">Qty</span>
            <span className="text-right font-mono font-medium">Sub.</span>
            <span className="text-right font-mono font-medium">Total</span>
          </div>
        </div>

        <Separator />

        {data?.order_items?.map((item: any, index: number) => (
          <div key={item?.id} className={cn('flex w-full flex-col gap-1 pt-2')}>
            <div className="grid grid-cols-[1fr_10fr]">
              <span className="font-mono">{index + 1}</span>
              <div className="grid grid-cols-[5fr_1fr_2fr_2fr]">
                <div className="flex w-full flex-col">
                  <span className="font-mono">
                    {item?.variant_name} / {item?.product_name}
                  </span>
                  {item?.discount_total > 0 && (
                    <span className="font-mono text-sm font-medium text-green-500">
                      ({item?.discount_total} off)
                    </span>
                  )}
                </div>
                <span className="font-mono">{item?.sold_quantity}</span>
                <span className="text-right font-mono">
                  {formatPrice(item?.sub_total, 2, false)}
                </span>
                <span className="text-right font-mono">
                  {formatPrice(item?.total, 2, false)}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-[1fr_10fr]">
              <span className="font-mono" />
              {item?.addon_options?.length > 0 && (
                <div className="w-full">
                  <div className="relative flex w-full py-3">
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 bg-white py-1 pr-2 text-xs font-medium text-gray-600">
                      Addons
                    </span>
                    <Separator />
                  </div>
                  <div className="flex w-full flex-col gap-2">
                    {item?.addon_options?.map((addon: any) => (
                      <div
                        key={addon?.id}
                        className="flex w-full items-center justify-between gap-2"
                      >
                        <span className="text-sm font-medium">
                          {addon?.name}
                        </span>
                        <span className="text-sm font-medium">
                          {formatPrice(addon?.price, 2, false)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {index !== data?.order_items?.length - 1 && <Separator />}
          </div>
        ))}
      </div>

      <Separator />

      <div className="flex w-full flex-col gap-1">
        {data?.order_totals?.map((item: any, index: number) => {
          if (item?.amount === 0) return null
          return (
            <div className="grid grid-cols-2" key={index}>
              <span className="font-mono">{item?.total_name}</span>
              <span className="text-right font-mono">
                {formatPrice(item?.amount, 2, false)}
              </span>
            </div>
          )
        })}
      </div>

      <Separator />
      <span className="font-mono text-sm font-medium">Payments</span>
      <Separator />

      <div className="flex w-full flex-col gap-1">
        {data?.payment_summary?.payments?.map((item: any, index: number) => {
          if (item?.amount_paid === 0) return null
          return (
            <div className="grid grid-cols-2" key={index}>
              <span className="font-mono capitalize">{item?.code}</span>
              <span className="text-right font-mono">
                {formatPrice(item?.amount_paid, 2, false)}
              </span>
            </div>
          )
        })}
      </div>

      <Separator />

      <div className="flex w-full flex-col gap-1">
        <div className="grid grid-cols-2">
          <span className="font-mono">Customer Paid</span>
          <span className="text-right font-mono">
            {formatPrice(data?.payment_summary?.total_paid, 2, false)}
          </span>
        </div>
        <div className="grid grid-cols-2">
          <span className="font-mono">Balance to Customer</span>
          <span className="text-right font-mono">
            {formatPrice(data?.payment_summary?.over_paid, 2, false)}
          </span>
        </div>
      </div>
    </div>
  )
}
